import React, { useEffect, useState } from 'react';
import { RoleModel, UserAffiliatedCompanyModel } from 'services/EpdClient';
import EmptyUserAffiliatedCompaniesList from './EmptyUserAffiliatedCompaniesList';
import CompanyItem from './CompanyItem';
import RoleService from 'services/RoleService';
import CompanyService from 'services/CompanyService';
import { toaster } from 'components/v2/toast';
import LoadingSkeleton from '../LoadingSkeleton';
import styled from 'styled-components';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { Paginator, PaginatorPageChangeEvent } from 'primereact/paginator';
import RemoveRolesDialog from './remove-roles-dialog/RemoveRolesDialog';
import AuthService from 'services/AuthService';

type PageSizeOption = { label: string; value: number };

export const PAGE_SIZE_OPTIONS: PageSizeOption[] = [
  { label: '10 items', value: 10 },
  { label: '20 items', value: 20 },
  { label: '50 items', value: 50 },
];

const UserSettingsMyRolesTab: React.FC = () => {
  const [companiesPage, setCompaniesPage] = useState<{
    items: UserAffiliatedCompanyModel[];
    totalCount: number;
  } | null>(null);
  const [pagination, setPagination] = useState({ page: 1, pageSize: PAGE_SIZE_OPTIONS[0].value });
  const [first, setFirst] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const [isLeaveOrganisationDialogVisible, setIsLeaveOrganisationDialogVisible] = useState<boolean>(false);
  const [leaveOrganisationContext, setLeaveOrganisationContext] = useState<{
    companyId: string;
    companyName: string;
    roles: RoleModel[];
    isUserVerifierInHomeCompany: boolean;
  } | null>(null);
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[] | null>(null);
  const [isLeavingOrganisation, setIsLeavingOrganisation] = useState(false);

  const openLeaveOrganisationDialog = (
    companyId: string,
    companyName: string,
    roles: RoleModel[],
    isUserVerifierInHomeCompany: boolean
  ) => {
    setLeaveOrganisationContext({ companyId, companyName, roles, isUserVerifierInHomeCompany });
    setSelectedRoleIds(roles.length === 1 && roles[0].id ? [roles[0].id] : []);
    setIsLeaveOrganisationDialogVisible(true);
  };

  const closeLeaveOrganisationDialog = () => {
    setIsLeaveOrganisationDialogVisible(false);
    setSelectedRoleIds(null);
  };

  const toggleRole = (roleId: string) => {
    setSelectedRoleIds((prev) => {
      if (!prev) return [roleId];
      return prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId];
    });
  };

  const handleLeaveOrganisation = async () => {
    const roles = leaveOrganisationContext?.roles ?? [];
    if (roles.length > 1 && (!selectedRoleIds || selectedRoleIds.length === 0)) {
      toaster({ severity: 'error', summary: 'Please select at least one role to remove.' });
      return;
    }

    try {
      const roleIds = selectedRoleIds ?? [];
      setIsLeavingOrganisation(true);
      await CompanyService.removeUserRoles(leaveOrganisationContext!.companyId, { roleIds });
      closeLeaveOrganisationDialog();
      await fetchCompaniesPage();
      await AuthService.refreshUser();
      toaster({ severity: 'success', summary: 'Operation completed successfully!' });
    } catch {
      toaster({
        severity: 'error',
        summary: 'Something went wrong.',
        details: 'Please try again later or contact support.',
      });
    } finally {
      setIsLeavingOrganisation(false);
    }
  };

  const fetchCompaniesPage = async () => {
    try {
      setIsLoading(true);
      const result = await RoleService.getUserAffiliatedCompaniesPage(pagination.page, pagination.pageSize);
      setCompaniesPage({ items: result.items, totalCount: result.totalCount });
    } catch (error) {
      toaster({
        severity: 'error',
        summary: 'Something went wrong.',
        details: 'Please try again later or contact support.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCompaniesPage();
  }, [pagination.page, pagination.pageSize]);

  const handlePageChange = (event: PaginatorPageChangeEvent) => {
    setFirst(event.first);
    setPagination({
      page: event.page + 1,
      pageSize: event.rows,
    });
  };

  const handleRowsChange = (e: DropdownChangeEvent) => {
    const newPageSize = e.value as number;
    setFirst(0);
    setPagination({ page: 1, pageSize: newPageSize });
  };

  const paginatorTemplate = {
    layout: 'RowsPerPageDropdown CurrentPageReport PrevPageLink PageLinks NextPageLink',
    RowsPerPageDropdown: (options: any) => (
      <>
        <span className="paginator-show-items-label">Show:</span>
        <Dropdown
          value={pagination.pageSize}
          options={PAGE_SIZE_OPTIONS}
          onChange={handleRowsChange}
          className="paginator-dropdown"
        />
        <span className="paginator-total-count">Total count: {companiesPage?.totalCount ?? 0}</span>
      </>
    ),
    CurrentPageReport: <span style={{ marginLeft: 'auto' }} />,
  };

  if (companiesPage === null || isLoading) return <LoadingSkeleton />;

  const items = companiesPage.items;
  if (items.length === 0) return <EmptyUserAffiliatedCompaniesList />;

  return (
    <>
      {leaveOrganisationContext && (
        <RemoveRolesDialog
          context={leaveOrganisationContext}
          selectedRoleIds={selectedRoleIds}
          visible={isLeaveOrganisationDialogVisible}
          isLeavingOrganisation={isLeavingOrganisation}
          onHide={closeLeaveOrganisationDialog}
          onToggleRole={toggleRole}
          onLeave={handleLeaveOrganisation}
        />
      )}
      {items.map((company) => (
        <CompanyItem
          key={company.id}
          id={company.id}
          name={company.name}
          roles={company.roles}
          isUserAccountManager={company.isUserAccountManager}
          isUserVerifierInHomeCompany={company.isUserVerifierInHomeCompany}
          onLeave={openLeaveOrganisationDialog}
        />
      ))}
      <CompaniesPaginator
        template={paginatorTemplate}
        first={first}
        rows={pagination.pageSize}
        totalRecords={companiesPage.totalCount}
        onPageChange={handlePageChange}
        className="justify-content-start"
      />
    </>
  );
};

const CompaniesPaginator = styled(Paginator)`
  padding: 0;

  .paginator-show-items-label,
  .paginator-total-count {
    ${(props) => props.theme.typography.bodyInput}
    color: ${(props) => props.theme.colors.primaryBlack};
    font-size: 12px;
  }

  .paginator-total-count {
    margin-left: 12px;
  }

  .paginator-dropdown {
    background-color: transparent;
    padding: 0;
    align-items: center;
    border: none;
    outline: none;
    border-radius: 2px;
    box-shadow: none;
    font-weight: 600;
    font-family: 'Inter';
    & .p-inputtext {
      padding: 0;
      color: ${(props) => props.theme.colors.primaryBlack};
      justify-content: center;
      font-size: 12px;
    }
    & .p-dropdown-trigger {
      padding-top: 1px;
      color: ${(props) => props.theme.colors.primaryBlack};
      width: 10px;
      justify-content: center;
      margin-left: 8px;
    }
  }

  .paginator-dropdown.p-dropdown:not(.p-disabled).p-focus {
    outline: none !important;
    box-shadow: none;
    border: none;
  }
`;

export default UserSettingsMyRolesTab;
